import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { retry } from 'rxjs/operators';
import { Subject } from "rxjs/Subject";
import { Asset } from '../../interfaces/asset.model';
import { AssetType } from '../../interfaces/asset-type.model';
import { SynchroProgress } from '../../interfaces/synchro-progress.model';
import { FileProvider } from '../file/file';
import { HelperProvider } from '../helper/helper';
import { ENV } from '@app/env';
import { LocalfileProvider } from '../localfile/localfile';
import { Platform } from 'ionic-angular';
import { EmailPackageProvider } from '../email-package/email-package';

@Injectable()
export class AssetProvider {

  assets: Asset[];
  //assetsTypes: AssetType[];
  progress: SynchroProgress;
  directories: any[];
  assetsLength: number;
  isIosPlatform: boolean;
  wsUrl: string = ENV.wsUrl+'STSService/FFOService.svc/';

  constructor(
    public plt: Platform,
    public http: HttpClient, 
    public storage: Storage, 
    public fileService: FileProvider, 
    public helperService: HelperProvider,
    public localfileService: LocalfileProvider,
    public emailPackageService: EmailPackageProvider) {
    this.directories = ['assets', 'thumbnails', 'localfiles'];
    this.assets = null;
    // this.isIosPlatform = this.plt.is('ios');
    //[VP - 27/01/2021] Use custom function to detect iOS because of ionic bug: https://github.com/ionic-team/ionic-v3/issues/1104
    this.isIosPlatform = this.helperService.isIOS();
  }

  getAssets() {
    return new Promise<Asset[]>((resolve, reject) => {
      this.storage.get("assets").then((data:Asset[]) => {
        data = data || [];
        this.assets = data.filter(item => (item.Disabled == null) || (item.Disabled == false)); //[VP-05.12.2024] Exclude Disabled Assets (new property)
        resolve(this.assets);
      });
    });
  }

  updateAssetsInStorage() {
    this.storage.set("assets", this.assets);
  }

  createDirectoriesForAssets(directory){
    return new Promise((resolve, reject) => {
        this.fileService.checkDirectory(directory)
        .then(_ => {
          console.log('Directory exists');
          resolve('Directory exists');
        })
        .catch(err => {
          console.log('Directory doesn\'t exist');
          this.fileService.createDirectory(directory)
          .then(directoryEntry => {
              console.log('Success create new directory');
              console.log('directoryFullPath :' + directoryEntry.fullPath);
              resolve('Success create new directory');
          })
          .catch(err => {
              console.log('Error create new directory');
              reject('Error create new directory');
            }
          )
        });
    });
  }

  getAssetTypes() {
    return this.storage.get("assetsTypes");
  }

  getAssetTypeForAsset(assetTypeId: number) {
    return this.getAssetTypes().then((assetTypes: AssetType[]) => {
      if(assetTypes && assetTypes.length > 0){
        return assetTypes.find(item => item.AssetTypeId == assetTypeId);
      }
      return null;
    }).catch(err => {
      console.log("Error: impossible to get the assetTypes from the storage");
    });
  }

  getAllAssetsFile(subject: Subject<SynchroProgress>, progress:SynchroProgress, assets: Asset[]) {
    this.assetsLength = assets.length;

    let promises = this.directories.map(directory => {
      return this.createDirectoriesForAssets(directory);
    });
    
    //Add a timeout to prevent synchro lock in case @ionic-native/file promises never resolve (like it is the case sometimes with iOS13.2.3)
    console.log("Wait for all directories checking... Nb of Promises: ",promises.length);  
    let promiseAll = Promise.all(promises);
    this.helperService.promiseTimeout(5000, promiseAll)
    .then(res => {
      this.progress = progress;
      for(let i in assets) {
        let currentAsset = assets[i];
        
        this.getAssetFile(currentAsset.AssetId)
        .subscribe((response: Blob) => {
          let fileData = response;
          currentAsset.MimeType = fileData.type;
          this.saveFile(fileData, currentAsset, 'assets')
          .then(res => {
              this.progressSaveAssetFiles(subject);
              console.log("Success saveFile Asset: "+ res);
          })
          .catch(err => {
            console.log("Asset: "+err);
          });

        }, error => {
          console.log("error getAssetFile");
          this.progressSaveAssetFiles(subject);
        });

        if(currentAsset.Thumbnail !== null){
          this.getAssetThumbnail(currentAsset)
          .subscribe((response: Blob) => {
            let fileData = response;
            this.saveFile(fileData, currentAsset, 'thumbnails')
            .then(res => {
                console.log("Success saveFile Thumbnail: "+ res);
            })
            .catch(err => {
              console.log("Thumbnail: "+err);
            });
            
          }, error => {
            //TODO: HOW TO HANDLE ERRORS???
            let errorMsg = "Error getAssetThumbnail: "+ error;
            console.log(errorMsg);
          });
        }

      }
    })
    .catch(err => {
      console.log("Error to create asset directories:", err);
      this.progress = progress;
      this.progress.Finished = true;
      this.progress.Error = true;
      this.progress.ErrorDetails = err;
      subject.next(this.progress);
      subject.complete();
      
    });
  }

  getAssetFile(assetId: Number) {
      const urlGetAssetFile = `${this.wsUrl}FileDownload/GetAssetFile?assetId=`;
      return this.http.get(urlGetAssetFile+assetId, {responseType: 'blob', withCredentials: true})
      .pipe(
        retry(3) // retry a failed request up to 3 times
      );
      /*.subscribe((response: Blob) => {
        let fileData = response;
        currentAsset.MimeType = fileData.type; // Add the file MIME Type into the asset
        this.fileService.saveFile(fileData, currentAsset, 'assets');
        resolve(fileData);
      }, error => {
        //TODO: HOW TO HANDLE ERRORS???
        
        let errorMsg = "Error getAssetFile: "+ error;
        console.log(errorMsg);
        reject(errorMsg);
      });*/
  }

  getAssetThumbnail(currentAsset: Asset) {
      const urlGetAssetThumbnail = `${this.wsUrl}FileDownload/GetAssetThumbnail?assetId=`;
      return this.http.get(urlGetAssetThumbnail+currentAsset.AssetId, {responseType: 'blob', withCredentials: true});
  }

  saveFile(fileData, currentAsset, fileType){
    return this.fileService.saveFile(fileData, currentAsset, fileType);
  }

  progressSaveAssetFiles(subject: Subject<SynchroProgress>){
    this.progress.Current++;
    if (this.progress.Current == this.assetsLength) {
      this.progress.Finished = true;
      subject.next(this.progress);
      subject.complete();
    } else {
      subject.next(this.progress);
    }
  }


  /* SYNCHRO */
 
  updateAssetList(synchroListAssets){
    return new Promise((resolve, reject) => {
      let add = this.addAssets(synchroListAssets.Added);
      let del = this.deleteAssets(synchroListAssets.Deleted);
      let up = this.updateAssets(synchroListAssets.Updated);
      
      Promise.all([add, del, up]).then(data => { 
        this.storage.set("assets", this.assets).then(() => {
          resolve(data);
        });
      });
    });
  }

  addAssets(addedSyncList) {
    return new Promise((resolve, reject) => {
      if(addedSyncList.length > 0){
        for(let i = 0; i < addedSyncList.length; i++){
          let asset = addedSyncList[i];
          let found = this.assets.some(item => item.AssetId == asset.AssetId);
          if(!found){
              this.assets.push(asset);              
          }
        }
        resolve();
      } else {
        resolve();
      }
    });
  }

  deleteAssets(deletedSyncList) {
    return new Promise((resolve, reject) => {
      if(deletedSyncList.length > 0) {
        for(let i = 0; i < deletedSyncList.length; i++){
          const index = this.assets.findIndex(asset => asset.AssetId == deletedSyncList[i]);
          if(index !== -1) {
            this.emailPackageService.deleteAssetInAllPackage(this.assets[index].AssetId);
            let deleted = this.assets.splice(index, 1);

            if(this.isIosPlatform){
              //Delete files associated with the deleted asset
              this.deleteAssetFiles(deleted[0]);
            }
          }
        }
        resolve();
      } else {
        resolve();
      }
    });
  }

  updateAssets(updatedSyncList){
    return new Promise((resolve, reject) => {
      let deletePromises = [];
      if(updatedSyncList.length > 0){
        for(let i = 0; i < updatedSyncList.length; i++){
          let index = this.assets.findIndex(item => item.AssetId == updatedSyncList[i].AssetId)
          if(index !== -1){
            //Keep current Asset properties: favorite, lastOpen,...
            this.assets[index] = this.updateAssetProperties(this.assets[index], updatedSyncList[i]);

            if(this.isIosPlatform){
              //Launch promise for files deletion, including Local files, HTML directory and thumbnails
              deletePromises.push(this.deleteAssetFiles(this.assets[index]));
            }
          }
          //[VP-05.12.2024] ToDo: Deal with Updated assets not found
          else {
            //Asset updated but not existing? Add it to our list of assets!
            this.assets.push(updatedSyncList[i]);
          }
        }
        if(this.isIosPlatform){
          //wait for all delete promises to be finished
          Promise.all(deletePromises)
          .then(() => {
            resolve();
          })
          .catch(() => {
            resolve();
          });
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  updateAssetProperties(currentAsset, newAsset){
    let favoriteStatus = currentAsset.IsFavorite;
    let favoriteId = currentAsset.FavoriteId;
    let lastDateOpen = currentAsset.LastDateOpen;

    //Update asset object
    currentAsset = newAsset;
    currentAsset.IsFavorite = favoriteStatus;
    currentAsset.FavoriteId = favoriteId;
    currentAsset.LastDateOpen = lastDateOpen;

    return currentAsset;
  }

  synchroAssetsFile(subject: Subject<SynchroProgress>, progress:SynchroProgress, synchroListAssets){

    this.progress = progress;

    let addedAssetList = synchroListAssets.Added;
    let updatedAssetList = synchroListAssets.Updated;
    let concatAsstList = addedAssetList.concat(updatedAssetList);

    if(concatAsstList.length > 0) {
      this.getAllAssetsFile(subject, progress, concatAsstList);
    } else {
      this.progress.Finished = true;
      subject.next(this.progress);
      subject.complete();
    }
  }

  deleteAssetFiles(asset: Asset) {
    return new Promise((resolve, reject) => {
      
      if(typeof asset !== 'undefined'){

        const localfiles = this.localfileService.getLocalfileDataByAssetId(asset.AssetId);

        this.getAssetTypeForAsset(asset.AssetTypeId).then((assetType: any) => {

          if(assetType.AssetTypeCode == 'HTML'){
            this.fileService.removeDirectory(asset.AssetFile);
          }

          this.fileService.removeFile(asset.AssetFile, 'assets');

          if(asset.Thumbnail !== null) {
            this.fileService.removeFile(asset.Thumbnail, 'thumbnails');
          }

          if(localfiles && localfiles.length > 0) {
            this.deleteLocalfiles(localfiles);
          }

          resolve();
        });
      } else {
        console.log("Asset not found...");
        reject();
      }
    });
  }

  deleteLocalfiles(localfiles){
    for(var i = 0; i < localfiles.length; i++){
      this.fileService.removeFile(localfiles[i].fileName, 'localfiles')
    }
  }

  // Remove local files if the linked asset is updated 
  removeUpdatedLocalAssetFiles(updatedAssetList){
    for(let i in updatedAssetList) {
      let assetId = updatedAssetList[i].AssetId;
      const localfiles = this.localfileService.getLocalfileDataByAssetId(assetId);
      if(localfiles && localfiles.length > 0) {
        this.deleteLocalfiles(localfiles);
      }
    }
  }

  filterAssetsByCategory(selectedCategorie) {
    if(this.assets && this.assets.length > 0){
      return this.assets.filter(item => item.CategoryId == selectedCategorie);
    }
    return [];
  }

  filterAssetsLastDateOpen(){
    let arr:Asset[] = [];
    if(this.assets && this.assets.length > 0){
      arr = this.assets.filter(item => typeof(item.LastDateOpen) !== "undefined" && item.LastDateOpen !== null);
      arr.sort((a:Asset, b:Asset) => {
        return +new Date(b.LastDateOpen) - +new Date(a.LastDateOpen)
      });
    }
    return arr;
  }

  addAssetInFavorite(asset){
    asset.IsFavorite = true;
    this.storage.set("assets", this.assets);
  }

  removeAssetInFavorite(asset){
    asset.IsFavorite = false;
    asset.FavoriteId = null;
    this.storage.set("assets", this.assets);
  }

  setAssetLastDateOpen(asset){
    asset.LastDateOpen = this.helperService.getFullDate(2);
    this.storage.set("assets", this.assets);
  }

  hasAssetsInCategory(selectedCategorie){
    return this.assets.some(item => item.CategoryId == selectedCategorie);
  }

  getAssetDataById(assetId){
    return this.assets.find(asset => asset.AssetId == assetId);
  }

}



// WEBPACK FOOTER //
// ./src/providers/asset/asset.ts